import {
  Anchor,
  Button,
  Card,
  Center,
  Divider,
  Stack,
  ThemeIcon,
  Title,
  Code,
  TypographyStylesProvider,
} from "@mantine/core";

import React from "react";
import DocsHighlightCard from "../articlesHelpers/DocsHighlightCard";
import {
  IconArrowBigDownFilled,
  IconBolt,
  IconDatabase,
  IconExchange,
  IconRocket,
  IconTool,
  IconTools,
} from "@tabler/icons-react";
import DocsButton from "../articlesHelpers/DocsButton";

export const OUTBOUND_CAPACITY_CONTENT = (
  <TypographyStylesProvider>
    <p>
      In other guides, you might read about something called "liquidity". We
      think this term is confusing. We use the term "capacity" instead.
    </p>

    <p>
      Outbound Capacity is total amount of Bitcoin you are able to send on the
      Lightning Network. We calculate this by adding up the outbound capacity of
      your lightning channels.
    </p>

    <h4>
      Help! I received a 10,000 satoshis payment, but my outbound capacity is
      now only 9,864 satoshis!
    </h4>

    <p>
      Yes. This is because we are "hiding" some of your satoshis, about 1/2 of
      one percent (0.5%), with the expectation that you will pay network fees
      when you make payments. And your node's "reserve", which prevents you from
      bringing any channel balance to zero until you close the channel, is also
      included in the calculation.
    </p>
    <h4>How do I get more outbound capacity?</h4>

    <p>There are at least two ways to get outbound capacity:</p>

    <DocsHighlightCard>
      <Title order={4}>Get Outbound Capacity: Strategy #1</Title>
      <p>
        This strategy is very fast, but requires that you already have a BTC
        wallet that supports lightning payments.
      </p>
      <p> First, get inbound from Rizful:</p>
      <DocsButton
        ariaLabel="get inbound"
        text="Get Inbound"
        leftSection={<IconRocket />}
      />
      <p>
        {" "}
        Then, send a payment to yourself -- send a payment to the Rizful node
        you are currently using:
      </p>
      <DocsButton
        ariaLabel="receive lightning"
        text="Receive Lightning"
        leftSection={<IconBolt />}
      />
      <p>
        {" "}
        You can send it with an "invoice", or a "lightning address" -- either
        will work fine.{" "}
      </p>
      <p>
        Like magic, the funds that you receive on your Rizful node will now be
        added to your Outbound Capacity. Like, you sent it in, and you can send
        it out now, if you want!
      </p>
    </DocsHighlightCard>
    <DocsHighlightCard>
      <Title order={4}>Get Outbound Capacity: Strategy #2</Title>
      <p>
        {" "}
        This strategy is slower, but allows you to move BTC you have "on-chain"
        to the Lightning Network.
      </p>
      <p> First, deposit BTC:</p>
      <DocsButton
        ariaLabel="deposit btc"
        text="Deposit BTC"
        leftSection={<IconArrowBigDownFilled />}
      />
      <p> Then, use those deposited funds to open a channel:</p>
      <DocsButton
        ariaLabel="open channel to peer"
        text="Open Channel"
        leftSection={<IconExchange />}
      />

      <p>
        The entire size of the channel will then be added to your Outbound
        Capacity.
      </p>
    </DocsHighlightCard>

    <h4>But why does my outbound capacity keep changing slightly?</h4>
    <p>
      Don't worry: This is always confusing for new Lightning users! Your
      outbound balance will change slightly every few minutes even if you're not
      sending or receiving any payments.
    </p>
    <p>
      {" "}
      The changes will be small. If one minute you have 1,000 satoshis in
      outbound capacity, a few minutes later, you might have 1,010, or 990.
    </p>

    <p>
      This is because your Rizful node keeps "reserves" -- it doesn't allow you
      to spend every last satoshi of your balance. It keeps reserves to ensure
      that you can always pay the fees required to close the channel, if you
      decide to close it.
    </p>

    <p>
      And, here is the kicker. The <em>size</em> of the reserves depends on the
      current fees on the Bitcoin blockchain. When fees go up, your node keeps
      bigger reserves, thus seeming to reduce your balance, and when fees go
      down, your node keeps smaller reserves, thus seeming to increase your
      balance.
    </p>
    <p>
      {" "}
      To get a feel for the changing fees, you can watch{" "}
      <a href="https://mempool.space/" target="_blank">
        mempool.space
      </a>
      , and keep an eye on the <code>TRANSACTION FEES</code>. If you watch for a
      minute or so, you'll see that they are continually changing....{" "}
    </p>

    <h4>How can I see the total amount of the reserves?</h4>
    <p>
      This number can be confusing to look at, because, for one thing, it will
      be constantly changing. So we've hidden it a bit. But if you want to see
      it, first click the Tools button...
    </p>
    <DocsButton
      ariaLabel="Tools"
      text="Tools"
      leftSection={<IconTool stroke={1.5} />}
    />
    <p>... and then click the Node Data button..</p>
    <DocsButton
      ariaLabel="Node Data"
      text="Node Data"
      leftSection={<IconDatabase stroke={1.5} />}
    />
    <p>
      ... and look for the number labeled <Code>Outbound reserves</Code>
    </p>
  </TypographyStylesProvider>
);
